// Web Push client: "your book is ready".
// iOS grants push only to a Home Screen app and only from a tap; Android and
// desktop browsers grant it in a tab. Subscriptions are re-sent to the
// backend on every launch because iOS drops them silently after reboots or
// long idle spells. Payloads use the Declarative Web Push shape
// ({ web_push: 8030, notification: {...} }) so Safari 18.4+ can show them
// without waking the worker; the classic push handler in service-worker.js
// reads the same shape everywhere else.
import wasabiService from '../services/WasabiService';
import { getApiUrl } from '../services/apiConfig';
import { isIOS, isStandalone, labLog } from './platform';

const VAPID_STORAGE_KEY = 'byopdf:push:vapid';
const LAST_SYNC_KEY = 'byopdf:push:synced';

export const pushSupported = () => typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

/** 'granted' | 'denied' | 'default' | 'unsupported' | 'install-first' */
export const pushState = () => {
  if (!pushSupported()) return isIOS() && !isStandalone() ? 'install-first' : 'unsupported';
  if (isIOS() && !isStandalone()) return 'install-first';
  return Notification.permission;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

async function getVapidPublicKey() {
  if (process.env.REACT_APP_VAPID_PUBLIC_KEY) return process.env.REACT_APP_VAPID_PUBLIC_KEY;
  try {
    const cached = localStorage.getItem(VAPID_STORAGE_KEY);
    if (cached) return cached;
  } catch (e) { /* ignore */ }
  const response = await fetch(getApiUrl('/api/push/vapid-public-key'));
  if (!response.ok) throw new Error(`VAPID key unavailable (${response.status})`);
  const { publicKey } = await response.json();
  if (!publicKey) throw new Error('VAPID key missing');
  try { localStorage.setItem(VAPID_STORAGE_KEY, publicKey); } catch (e) { /* ignore */ }
  return publicKey;
}

async function sendSubscription(subscription) {
  const json = subscription.toJSON();
  await wasabiService.makeAuthenticatedRequest('/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      expirationTime: json.expirationTime || null,
      platform: isIOS() ? 'ios' : 'other',
      standalone: isStandalone()
    })
  });
  try { localStorage.setItem(LAST_SYNC_KEY, String(Date.now())); } catch (e) { /* ignore */ }
}

/**
 * Ask for permission (must be called from a tap) and register the
 * subscription with the backend. Resolves to the resulting pushState().
 */
export async function enablePush() {
  if (!pushSupported()) return pushState();
  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    labLog('push permission', permission);
    return permission;
  }
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const key = await getVapidPublicKey();
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key)
    });
  }
  await sendSubscription(subscription);
  labLog('push subscribed', { endpoint: subscription.endpoint.slice(0, 60) });
  return 'granted';
}

/** On every launch: re-send an existing subscription so the server's copy stays fresh. */
export async function resyncPush() {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return false;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return false;
    await sendSubscription(subscription);
    return true;
  } catch (e) {
    return false;
  }
}

export async function disablePush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    await wasabiService.makeAuthenticatedRequest('/api/push/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    }).catch(() => {});
    await subscription.unsubscribe();
  } catch (e) { /* ignore */ }
}
